import { widgetBootstrap } from 'api/bootstrap'
import { getEmbedConfiguration } from 'api/embed'
import { trackPageEvent } from 'api/events'
import { sendInitEvent } from 'api/metrics'
import store from 'config/store'
import { CONSENT_TARGETS } from 'constants/gdpr'
import { HOST_INTEGRATIONS } from 'constants/integrations'
import BootstrapFailedError from 'errors/bootstrapFailed'
import { getWidgetReadyPayload } from 'modules/api'
import { PageTrackEventAttributes } from 'modules/api/types'
import { evaluateCampaignConversations, getUnreadCampaignIds } from 'modules/campaigns'
import { receiveIneligibleCampaigns } from 'modules/campaigns/actions'
import {
  receiveBulkConversations
} from 'modules/conversations/actions'
import { receiveEmbedConfiguration } from 'modules/embed/actions'
import { generateIdentity } from 'modules/identity'
import { receiveSentryConfig } from 'modules/logging/actions'
import { receiveInitTimestamp } from 'modules/metrics/actions'
import { countSiteVisit } from 'modules/session'
import {
  receiveAccessToken, receiveChatSocketAuth, receiveContext, receiveCookieDomain, receiveEndUser,
  receiveGDPR, receiveGeo, receivePresenceSocketAuth, receiveSession
} from 'modules/session/actions'
import { isWaitingForUserJwt } from 'modules/session/utils'
import { ChatSocketManager, VisitorPresenceSocketManager } from 'modules/socket'
import { setShouldForceShowWidget, setShouldSkipCampaigns } from 'modules/targeting/actions'
import { evalTargetingAndEnrollment } from 'modules/targeting/utils'
import { toggleInitComplete } from 'modules/view/actions'
import { contains, includes, omit, path, pathOr } from 'ramda'
import { PUBLIC_EVENTS } from 'shared/constants/api'
import { UserIdentity } from 'types/auth'
import {
  BootstrapTargetingContext, BootstrapTokenResponse, WidgetBootstrapParams, WidgetBootstrapResponse,
  WidgetChatBootstrapResponse
} from 'types/bootstrap'
import { Embed } from 'types/embed'
import { ServerTargetingEvaluationResults } from 'types/targeting'
import { error, interval, log } from 'utils'
import { hasGate } from 'utils/access'
import { fetchWindowContext } from 'utils/context'
import { resolveCookieDomain } from 'utils/cookies'
import { createEvent } from 'utils/events'
import { initI18n } from 'utils/i18n'
import { broadcast } from 'utils/message'
import { counterMetric, timingMetric } from 'utils/metrics'
import { fireByDriftlink } from 'utils/targeting'
import { tracedMethod } from 'utils/trace'

function Bootstrap({ embedId, params }: InitParams): void {
  this.dispatch = store.dispatch
  this.state = store.getState

  this.embedId = embedId
  this.params = params

  return this
}

Bootstrap.prototype.dataProtection = async function () {
  const { embed = {}, session = {} } = this.state()
  const { gdpr, geo = {} } = session
  const { country } = geo
  const { dataProtection } = embed?.configuration

  const resolveNeedsConsent = target => {
    if (target === "ALL") { return true }
    if (target === "NONE" || !target) { return false }

    if (!country) { return true }

    return includes(country, CONSENT_TARGETS[target])
  }

  if (!dataProtection) {
    return this.dispatch(receiveGDPR({
      needsConsent: false,
      hasConsent: true
    }))
  }

  const needsConsent = resolveNeedsConsent(dataProtection.targeting)
  const hasConsent = needsConsent ? gdpr.hasConsent : true

  this.dispatch(receiveGDPR({
    needsConsent,
    hasConsent
  }))
}

Bootstrap.prototype.hydrateContext = async function () {
  const context = await fetchWindowContext()
  this.dispatch(receiveContext(context))
  return context
}

Bootstrap.prototype.sendPageTrackEvent = function (context: Record<string, any>) {
  const pageAttributes: PageTrackEventAttributes = {
    url: pathOr('',  ['page', 'href'], context),
    referrer: pathOr('', ['page', 'referrer'], context),
    title: pathOr('', ['page', 'title'], context),
  }

  trackPageEvent(pageAttributes)
}

Bootstrap.prototype.fetchPreRequisiteData = async function () {
  let embedConfiguration: Partial<Embed> = {}

  this.dispatch(receiveSession({
    session: this.params.session
  }))

  try {
    const { data, responseTime = 0 } = await getEmbedConfiguration(this.embedId)
    embedConfiguration = data

    this.dispatch(receiveEmbedConfiguration(embedConfiguration))
    timingMetric('Bootstrap.embed_fetch', responseTime)

  } catch (err) {
    error(new BootstrapFailedError(`Embed Failed to load correctly - ${err.message}`), err)
  }

  try {
    const resolvedCookieDomain = resolveCookieDomain(pathOr(null, ['configuration', 'theme', 'cookieDomains'], embedConfiguration))
    if (!!resolvedCookieDomain) {
      store.dispatch(receiveCookieDomain({ cookieDomain: resolvedCookieDomain }))
    }
  } catch (err) {
    error(new BootstrapFailedError(`Failed to set custom cookie domain - ${err.message}`), err)
  }

  const setupI18n = async () => {
    try {
      const locale = pathOr<string>('en', ['configuration', 'locale'], embedConfiguration)
      await initI18n(locale)
    } catch (err) {
      log({ data: [`i18n failed to load - ${err.message}`, err], type: 'warn' })
    }
  }

  await setupI18n()
}

Bootstrap.prototype.handleBootstrap = async function () {
  const { embed, session } = this.state()
  const pageUrl = pathOr<string>('', ['context', 'page', 'href'], session)
  const sessionId: string = session.campaignRefreshToken
  const orgId: number = embed.orgId

  let tokenResult: BootstrapTokenResponse = null
  let chatResponse: WidgetChatBootstrapResponse = null
  let targetingResults: ServerTargetingEvaluationResults = null

  try {
    const identifierMap: UserIdentity = await generateIdentity()
    const { result } = await tracedMethod(async () => await callBootstrapApi({
      orgId,
      embedId: this.embedId,
      sessionId,
      identifierMap,
      pageUrl
    }), 'widget_core:bootstrap_api')

    tokenResult = result.tokenResult
    const chatAuth = result.chatAuth || null
    const presenceAuth = result.presenceAuth || null
    const sentryConfig = result.sentryConfig || null
    chatResponse = result.widgetChatBootstrapResponse

    this.dispatch(receiveGeo(result.location))

    if (chatAuth) {
      this.dispatch(receiveChatSocketAuth(chatAuth))
    }

    if (presenceAuth) {
      this.dispatch(receivePresenceSocketAuth(presenceAuth))
    }

    if (sentryConfig) {
      this.dispatch(receiveSentryConfig({
        forceLog: contains(orgId,sentryConfig.allowListOrgIds),
        limit: sentryConfig.limit
      }))
    }

    if (chatResponse) {
      evaluateCampaignConversations(chatResponse.campaignConversations)
      const alreadyEnrolledCampaignIds = Object.keys(chatResponse.campaignEnrollment)

      if (!!alreadyEnrolledCampaignIds.length) {
        this.dispatch(receiveIneligibleCampaigns(alreadyEnrolledCampaignIds))
      }

      if (!!chatResponse.conversations.length) {
        this.dispatch(receiveBulkConversations({
          conversations: chatResponse.conversations
        }))
      }
    }

    targetingResults = result.evaluatedConditionsResults
  } catch (err) {
    error(new BootstrapFailedError(`Failed Bootstrap API response - ${err.message}`), err)
  }

  if (!tokenResult) {
    return
  }

  const { accessToken, didConsentToRequestedConsentId, assignedAgentId } = tokenResult
  const endUser = omit(['didConsentToRequestedConsentId', 'accessToken', 'assignedAgentId'], tokenResult)

  this.dispatch(receiveEndUser({ endUser, assignedAgentId: assignedAgentId }))
  this.dispatch(receiveAccessToken({ accessToken }))
  this.dispatch(receiveGDPR({
    hasConsent: didConsentToRequestedConsentId || false
  }))

  this.handleSockets({ orgId, accessToken })

  const hasFiredDriftLink = await this.firePossibleDriftLink()
  const unreadCampaignIds = chatResponse ? getUnreadCampaignIds(chatResponse.campaignConversations) : []

  evalTargetingAndEnrollment({
    evaluatedConditionsResults: targetingResults,
    unreadCampaignIds,
    skipCampaignTargeting: hasFiredDriftLink
  })
}

Bootstrap.prototype.handleSockets = async function ({
  orgId,
  accessToken
}: { orgId: number, accessToken: string }) {
  await tracedMethod(() => ChatSocketManager.connect({ accessToken, orgId }),
    'widget_core.sockets:chat_connect')
}

Bootstrap.prototype.firePossibleDriftLink = async () => {
  const { data: hostWindow = {} } = await broadcast({
    topic: 'get-window-context'
  })

  const urlHash = pathOr('', ['location', 'hash'], hostWindow)
  const fired = await fireByDriftlink(urlHash)
  return fired
}

Bootstrap.prototype.connectToPresence = function () {
  const { accessToken } = this.state().session.auth
  const { orgId } = this.state().embed

  if (!orgId || !accessToken) {
    return
  }

  tracedMethod(() => VisitorPresenceSocketManager.connect({ accessToken, orgId }),
    'widget_core.sockets:visitor_presence_connect')
}

Bootstrap.prototype.attachIntegrations = async function () {
  const { configuration = {} } = this.state().embed
  const { integrations = {} } = configuration

  Object.keys(integrations)
    .forEach(key => {
      if (HOST_INTEGRATIONS[key]) {
        broadcast({
          topic: 'handle-host-integration',
          message: { config: integrations[key], type: key }
        })
      }
    })
}

Bootstrap.prototype.waitForUserJwt = function () {
  const stopInterval = interval(() => {
    console.warn('Drift Widget is still waiting for user JWT!')
  }, 30000)

  return new Promise((resolve) => {
    const unsubscribe = store.subscribe(async () => {
      if (await isWaitingForUserJwt()) {
        return
      }
      stopInterval()
      unsubscribe()
      resolve(() => {})
    })
  })
}

interface InitParams {
  embedId: string,
  params: {
    skipCampaigns: boolean,
    forceShow: boolean,
    session: {
      instanceId: string,
      sessionId: string,
      sessionStarted: number,
      campaignRefreshToken: string
    }
  },
  timing: {
    pageLoadStartTime?: string
  }
}

export const init = async (params: InitParams) => {
  const bootstrap = new Bootstrap(params)
  const shouldSkipCampaigns = pathOr(false, ['params', 'skipCampaigns'], params)
  const shouldForceShowWidget = pathOr(false, ['params', 'forceShow'], params)
  const sessionId = pathOr(null, ['params', 'session', 'sessionId'], params)
  const instanceId = pathOr(null, ['params', 'session', 'instanceId'], params)

  bootstrap.dispatch(setShouldSkipCampaigns(shouldSkipCampaigns))
  bootstrap.dispatch(setShouldForceShowWidget(shouldForceShowWidget))

  const measuredInit = async () => {
    try {
      await tracedMethod(() => bootstrap.fetchPreRequisiteData(), 'bootstrap: embed')
      if (await isWaitingForUserJwt()) {
        await tracedMethod(() => bootstrap.waitForUserJwt(), 'bootstrap: waitForUserJwt')
      }

      await tracedMethod(() => bootstrap.handleBootstrap(), 'bootstrap: bootstrap')
      await tracedMethod(() => bootstrap.dataProtection(), 'bootstrap: GDPR')

      fireWidgetReadyEvent()

      bootstrap.dispatch(toggleInitComplete({
        initComplete: true
      }))
    } catch (err) {
      counterMetric('bootstrap.failure')
      error(`Failed to bootstrap - ${err.message}`, err)
    }
  }

  try {
    await countSiteVisit()
    const context = await bootstrap.hydrateContext()

    if (sessionId) {
      fireWidgetInitEvent(params.embedId, sessionId, instanceId, params.timing.pageLoadStartTime, context)
    }

    const { timing: bootstrapDuration } = await tracedMethod(measuredInit, 'bootstrap')
    timingMetric('bootstrap', bootstrapDuration)

    bootstrap.sendPageTrackEvent(context)
    bootstrap.attachIntegrations()
    if (!hasGate('has_presence_disabled')) {
      bootstrap.connectToPresence()
    }
    Promise.resolve('bootstrap complete')
  } catch (err) {
    error(new Error(err))
    Promise.reject('bootstrap failed')
  }
}

/**
 * Fires the widget ready event with some information about the redux state
 * To give enough time for targeting to complete and update the redux store, we wait one second before firing the event
 */
const fireWidgetReadyEvent = async () => {
  setTimeout(async () => {
    const readyPayload = await getWidgetReadyPayload()
    createEvent(PUBLIC_EVENTS.READY, readyPayload)
  }, 1000)
}

const fireWidgetInitEvent = (embedId: string, sessionId: string, instanceId: string, pageLoadStartTime: string, context: Record<string, any> = {}) => {
  const pageUrl = path(['page', 'url'], context)
  const hostname = path(['page', 'hostname'], context)


  const event = {
    eventName: `[V2] - init`,
    embedId,
    sessionId,
    instanceId,
    context: {
      url: pageUrl,
      hostname: hostname,
      timezone: context.timezone,
      userAgent: context.userAgent
    }
  }

  if (pageLoadStartTime) {
    event.context['timeToInit'] = Date.now() - parseInt(pageLoadStartTime)
  }

  store.dispatch(receiveInitTimestamp(Date.now()))
  sendInitEvent(event)
    .catch(err => {
      log({ type: "warn", data: ['Failed to send init event'] })
    })
}

interface CallBootstrapApiParams {
  orgId: number,
  embedId: string,
  sessionId: string,
  identifierMap: UserIdentity
  pageUrl: string
}

const callBootstrapApi = async ({
  orgId,
  embedId,
  sessionId,
  identifierMap,
  pageUrl
}: CallBootstrapApiParams): Promise<WidgetBootstrapResponse> => {
  const targetingContext: BootstrapTargetingContext = {
    sessionId,
    pageUrl,
    // TODO: hasHadConversations and followingCampaignIds required for targeting but should be resolved server-side
    hasHadConversations: false,
    followingCampaignIds: [],
    excludedCampaignIds: []
  }

  const params: WidgetBootstrapParams = {
    ...identifierMap,
    embed_id: embedId,
    targeting_context: targetingContext
  }

  const { data: bootstrapResponse, responseTime } = await widgetBootstrap(orgId, params)
  timingMetric('enduser_bootstrap', responseTime)

  return bootstrapResponse
}
